// Tab strip for the exotics pricer. Renders the Multi-token tab plus any
// custom token-locked tabs, an ellipsis menu per custom tab for
// rename/duplicate/delete, and a "+" button that opens the New Tab modal.
//
// Visual language matches the Diletta panel chrome already used by the
// risk-ladder modal and the trade band (#1f3645 banner bg, #c2e5ff title,
// #026aa2 accent border).
import { FC, useContext, useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Stack, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus,
  faEllipsisV,
  faPenToSquare,
  faClone,
  faTrash,
} from '@fortawesome/free-solid-svg-icons';

import {
  ExoticsTabsContext,
  MULTI_TOKEN_TAB_KEY,
  isCustomTabKey,
  lockedTokenFromTabKey,
  labelFromTabKey,
} from '../../providers/exotics-tabs/exotics-tabs-provider';
import HandleExoticsTabModal, { TabModalView } from './handle-exotics-tab-modal';

const TAB_BORDER = '#1f3645';
const TAB_ACTIVE_BG = 'rgba(40, 114, 226, 0.10)';
const TAB_ACTIVE_BORDER = '#026aa2';
const TAB_ACTIVE_TEXT = '#c2e5ff';

const ExoticsTabsBar: FC = () => {
  const { tabs, currentTab, setCurrentTab } = useContext(ExoticsTabsContext);

  const [modalView, setModalView] = useState<TabModalView>(TabModalView.NEW_TAB);
  const [modalOpen, setModalOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuTabKey, setMenuTabKey] = useState<string | null>(null);

  const openModal = (view: TabModalView, key: string) => {
    setMenuAnchor(null);
    setModalView(view);
    setMenuTabKey(key);
    setModalOpen(true);
  };

  const openMenu = (e: React.MouseEvent<HTMLElement>, key: string) => {
    e.stopPropagation();
    setMenuTabKey(key);
    setMenuAnchor(e.currentTarget);
  };

  const closeMenu = () => setMenuAnchor(null);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.5}
        sx={{
          px: 2,
          py: 1,
          borderBottom: `1px solid ${TAB_BORDER}`,
          backgroundColor: 'transparent',
          overflowX: 'auto',
        }}
      >
        {tabs.map((tab) => {
          const isActive = tab.key === currentTab;
          const isCustom = isCustomTabKey(tab.key);
          const tok = lockedTokenFromTabKey(tab.key);
          const display = isCustom ? labelFromTabKey(tab.key, tab.label) : tab.label;

          return (
            <Box
              key={tab.key}
              role="tab"
              aria-selected={isActive}
              onClick={() => setCurrentTab(tab.key)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                px: 1.5,
                py: 0.5,
                borderRadius: '4px',
                border: `1px solid ${isActive ? TAB_ACTIVE_BORDER : 'transparent'}`,
                backgroundColor: isActive ? TAB_ACTIVE_BG : 'transparent',
                cursor: 'pointer',
                fontSize: 11,
                letterSpacing: '0.06em',
                color: isActive ? TAB_ACTIVE_TEXT : 'text.secondary',
                fontWeight: isActive ? 600 : 500,
                whiteSpace: 'nowrap',
                transition: 'background-color 120ms ease, color 120ms ease',
                '&:hover': { color: isActive ? TAB_ACTIVE_TEXT : 'text.primary' },
              }}
            >
              {tok && (
                <Typography
                  component="span"
                  sx={{
                    fontSize: 9,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    color: isActive ? TAB_ACTIVE_TEXT : 'text.disabled',
                  }}
                >
                  {tok}
                </Typography>
              )}
              <Typography component="span" sx={{ fontSize: 11, color: 'inherit' }}>
                {display}
              </Typography>
              {/* Standard ⋮ tab-options menu (Rename / Duplicate / Delete) for
                  EVERY tab except the pinned Multi-token base tab. This is the
                  original custom-tab affordance, now also applied to the MP
                  handoff tabs (MP_HANDOFF_<token>), the legacy single "from
                  Market Probabilities" tab, and any stale/unknown orphan key in
                  localStorage — one consistent close pattern, so no tab can get
                  stuck. */}
              {isActive && tab.key !== MULTI_TOKEN_TAB_KEY && (
                <IconButton
                  size="small"
                  onClick={(e) => openMenu(e, tab.key)}
                  aria-label={`tab-menu-${tab.key}`}
                  sx={{ p: 0.25, ml: 0.5, color: 'inherit' }}
                >
                  <FontAwesomeIcon icon={faEllipsisV} style={{ fontSize: 10 }} />
                </IconButton>
              )}
            </Box>
          );
        })}

        <Tooltip title="New tab">
          <IconButton
            size="small"
            onClick={() => openModal(TabModalView.NEW_TAB, MULTI_TOKEN_TAB_KEY)}
            aria-label="new-tab"
            sx={{
              ml: 0.5,
              p: 0.5,
              color: 'text.secondary',
              border: `1px dashed ${TAB_BORDER}`,
              borderRadius: '4px',
              '&:hover': { color: 'primary.light', borderColor: 'primary.light' },
            }}
          >
            <FontAwesomeIcon icon={faPlus} style={{ fontSize: 10 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => menuTabKey && openModal(TabModalView.RENAME_TAB, menuTabKey)}
          sx={{ fontSize: 12 }}
        >
          <FontAwesomeIcon icon={faPenToSquare} style={{ fontSize: 11, marginRight: 8 }} />
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => menuTabKey && openModal(TabModalView.DUPLICATE_TAB, menuTabKey)}
          sx={{ fontSize: 12 }}
        >
          <FontAwesomeIcon icon={faClone} style={{ fontSize: 11, marginRight: 8 }} />
          Duplicate
        </MenuItem>
        <MenuItem
          onClick={() => menuTabKey && openModal(TabModalView.DELETE_TAB, menuTabKey)}
          sx={{ fontSize: 12, color: 'error.main' }}
        >
          <FontAwesomeIcon icon={faTrash} style={{ fontSize: 11, marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>

      <HandleExoticsTabModal
        open={modalOpen}
        view={modalView}
        currentTabKey={menuTabKey ?? currentTab}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};

export default ExoticsTabsBar;
